import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useBusinessDashboardMetrics, BusinessDashboardRecord } from "@/hooks/admin/BusinessDashboardTab/useBusinessDashboardMetrics";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import MetricCard from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/MetricCard";
import BusinessIncomeExpensesChart from "@/components/v2/molecules/admin/tabs/BusinessDashboardTab/BusinessIncomeExpensesChart";
import BusinessProfitChart from "@/components/v2/molecules/admin/tabs/BusinessDashboardTab/BusinessProfitChart";
import BusinessDashboardDetailModal from "@/components/v2/molecules/admin/tabs/BusinessDashboardTab/BusinessDashboardDetailModal";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { isContractActive } from "@/utils/contractUtils";
import OrganizationsMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect";
import PropertiesMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const BusinessDashboardTab = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedIncomeRecords, setSelectedIncomeRecords] = useState<BusinessDashboardRecord[]>([]);
  const [selectedExpensesRecords, setSelectedExpensesRecords] = useState<BusinessDashboardRecord[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  const availableYears = [currentYear + 1, ...Array.from({ length: 6 }, (_, index) => currentYear - index)];
  
  const { data: properties = [], isLoading: isLoadingProperties } = useProperties();
  const { data: organizations = [], isLoading: isLoadingOrganizations } = useOrganizations();
  const { data: contracts = [], isLoading: isLoadingContracts } = useContracts();

  const filteredProperties = useMemo(() => {
    if (!selectedOrganizations.length) {
      const activePropertyIds = new Set(
        contracts.filter(c => isContractActive(c)).map(c => c.property_id)
      );
      return properties.filter(p => activePropertyIds.has(p.id));
    }
    const orgPropertyIds = contracts
      .filter(c => selectedOrganizations.includes(c.organization_id || '') && isContractActive(c))
      .map(c => c.property_id);
    return properties.filter(p => orgPropertyIds.includes(p.id));
  }, [properties, contracts, selectedOrganizations]);
  
  const { data: metrics, isLoading, error } = useBusinessDashboardMetrics(
    selectedYear,
    selectedProperties.length ? selectedProperties : undefined,
    selectedOrganizations.length ? selectedOrganizations : undefined
  );

  const shortenMonthName = (monthName: string): string => {
    const monthMap: Record<string, string> = {
      'Enero': 'Ene',
      'Febrero': 'Feb',
      'Marzo': 'Mar',
      'Abril': 'Abr',
      'Mayo': 'May',
      'Junio': 'Jun',
      'Julio': 'Jul',
      'Agosto': 'Ago',
      'Septiembre': 'Sep',
      'Octubre': 'Oct',
      'Noviembre': 'Nov',
      'Diciembre': 'Dic'
    };
    return monthMap[monthName] || monthName;
  };

  const chartIncomeData = useMemo(() => {
    return metrics?.monthly_income.map(item => ({
      ...item,
      month: shortenMonthName(item.month)
    })) || [];
  }, [metrics?.monthly_income]);

  const chartExpensesData = useMemo(() => {
    return metrics?.monthly_expenses.map(item => ({
      ...item,
      month: shortenMonthName(item.month)
    })) || [];
  }, [metrics?.monthly_expenses]);

  const chartProfitData = useMemo(() => {
    return metrics?.monthly_profit.map(item => ({
      ...item,
      month: shortenMonthName(item.month)
    })) || [];
  }, [metrics?.monthly_profit]);

  const handleBarClick = (month: string, incomeRecords: BusinessDashboardRecord[], expensesRecords: BusinessDashboardRecord[]) => {
    setSelectedMonth(month);
    setSelectedIncomeRecords(incomeRecords);
    setSelectedExpensesRecords(expensesRecords);
    setShowDetailModal(true);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive text-sm">
            Error al cargar los datos del dashboard. Por favor, intenta de nuevo.
          </p>
        </div>
      </div>
    );
  }

  const isLoadingFilters = isLoadingProperties || isLoadingOrganizations || isLoadingContracts;

  return (
    <div className="space-y-6">
      {/* Header: Title + Filters */}
      <div className="space-y-3">
        <div className="flex justify-between items-center min-h-10">
          <h2 className="text-2xl font-bold">Dashboard del Negocio</h2>
          <div />
        </div>
        
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <OrganizationsMultiSelect
              organizations={organizations}
              selectedOrganizations={selectedOrganizations}
              onSelectionChange={setSelectedOrganizations}
            />
            <PropertiesMultiSelect
              properties={filteredProperties}
              selectedProperties={selectedProperties}
              onSelectionChange={setSelectedProperties}
            />
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => setSelectedYear(parseInt(value))}
            >
              <SelectTrigger className="rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 font-medium text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      {isLoading || isLoadingFilters ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <MetricCard
            title="Ingresos Totales"
            icon={<TrendingUp className="h-5 w-5" />}
            value={formatCurrency(metrics?.total_income || 0)}
            description="Total de ingresos del año"
            variant="success"
            isLoading={false}
          />
          <MetricCard
            title="Egresos Totales"
            icon={<TrendingDown className="h-5 w-5" />}
            value={formatCurrency(metrics?.total_expenses || 0)}
            description="Total de egresos del año"
            variant="destructive"
            isLoading={false}
          />
          <MetricCard
            title="Utilidad Neta"
            icon={<DollarSign className="h-5 w-5" />}
            value={formatCurrency(metrics?.net_profit || 0)}
            description="Ingresos menos egresos"
            variant={(metrics?.net_profit || 0) >= 0 ? "success" : "destructive"}
            isLoading={false}
          />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ingresos/Egresos al Mes</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-80">
                <Skeleton className="w-full h-full" />
              </div>
            ) : (
              <BusinessIncomeExpensesChart
                incomeData={chartIncomeData}
                expensesData={chartExpensesData}
                onBarClick={handleBarClick}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Utilidad por Mes</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-80">
                <Skeleton className="w-full h-full" />
              </div>
            ) : (
              <BusinessProfitChart data={chartProfitData} />
            )}
          </CardContent>
        </Card>
      </div>

      <BusinessDashboardDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        month={selectedMonth}
        incomeRecords={selectedIncomeRecords}
        expensesRecords={selectedExpensesRecords}
      />
    </div>
  );
};

export default BusinessDashboardTab;